import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as SuccessIcon } from './assets/icons/success.svg';
import { ReactComponent as ErrorIcon } from './assets/icons/error.svg';
import { ReactComponent as InfoIcon } from './assets/icons/info.svg';
import { ReactComponent as WarningIcon } from './assets/icons/warning.svg';

import { Close, CloseContainer, IconContainer, LogoIcon, SplashContainer, StyledToastContainer } from './App.styles';

import PrivateLayout from './layout/PrivateLayout';
import SplashScreen from './components/Splash/Splash';

import LoginPage from './screens/OnboardingPages/LoginPage/LoginPage';
import ForgotPasswordPage from './screens/OnboardingPages/ForgotPasswordPage/ForgotPasswordPage';
import SetUpNewPasswordPage from './screens/OnboardingPages/SetUpNewPasswordPage/SetUpNewPasswordPage';
import DashboardPage from './screens/DashboardPage/DashboardPage';
import VerificationEmailPage from './screens/OnboardingPages/VerificationEmailPage/VerificationEmailPage';
import CreateDeliveryPage from './screens/CreateDeliveryPage/CreateDeliveryPage';
import SignupPage from './screens/OnboardingPages/SignupPage/SignupPage';
import PartnersPage from './screens/PartnersPage/PartnersPage';
import ProductsPage from './screens/ProductsPage/ProductsPage';
import ProductDetailsPage from './screens/ProductDetailsPage/ProductDetailsPage';
import ApproveProductPage from './screens/ApproveProductPage/ApproveProductPage';
import OrderDetailsPage from './screens/OrderDetailsPage/OrderDetailsPage';
import OrdersPage from './screens/OrdersPage/OrdersPage';

import { refresh } from './store/slices/auth/asyncThunks';
import { setLoggedIn } from './store/slices/auth/slice';
import { selectedLocale } from './store/slices/common/slice';
import { fetchUserInfo } from './store/slices/user/asyncThunks';
import LogisticsPage from './screens/LogisticsPage/LogisticsPage';
import DeliveryDetailsPage from './screens/DeliveryDetailsPage/DeliveryDetailsPage';
import InventoryPage from './screens/InventoryPage/InventoryPage';

function App() {
  const dispatch = useDispatch();
  const [minimumDurationPassed, setMinimumDurationPassed] = useState(false);
  const [isInitialFetchCompleted, setIsInitialFetchCompleted] = useState(false);
  const locale = useSelector(selectedLocale);

  const ar = locale === 'ar';

  useEffect(() => {
    const timer = setTimeout(() => setMinimumDurationPassed(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  document.documentElement.lang = locale;
  useEffect(() => {
    if (locale === 'ar') {
      document.body.style.direction = 'rtl';
    } else {
      document.body.style.direction = 'ltr';
    }
  }, [locale]);

  useEffect(() => {
    dispatch(refresh())
      .unwrap()
      .then(() => {
        dispatch(fetchUserInfo())
          .unwrap()
          .then(() => {
            dispatch(setLoggedIn({ loggedIn: true }));
            setIsInitialFetchCompleted(true);
          })
          .catch(err => {
            setIsInitialFetchCompleted(true);
          });
      })
      .catch(() => {
        dispatch(setLoggedIn({ loggedIn: false }));
        setIsInitialFetchCompleted(true);
      });
  }, [dispatch]);

  return (
    <Fragment>
      {isInitialFetchCompleted && minimumDurationPassed ? (
        <>
          <StyledToastContainer
            position={locale !== 'en' ? 'top-left' : 'top-right'}
            autoClose={6000}
            hideProgressBar={false}
            rtl={locale !== 'en'}
            newestOnTop
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            closeButton={({ closeToast }) => (
              <CloseContainer>
                <Close $ar={ar} />
                <LogoIcon onClick={closeToast} />
              </CloseContainer>
            )}
            icon={({ closeToast, type }) => (
              <IconContainer type={type} $ar={ar}>
                {type === 'success' && <SuccessIcon onClick={closeToast} />}
                {type === 'error' && <ErrorIcon onClick={closeToast} />}
                {type === 'warning' && <WarningIcon onClick={closeToast} />}
                {type === 'info' && <InfoIcon onClick={closeToast} />}
              </IconContainer>
            )}
          />

          <Routes>
            <Route path="/sign-up" element={<SignupPage />} />
            <Route path="/" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/set-up-new-password" element={<SetUpNewPasswordPage />} />
            <Route path="/verify-email" element={<VerificationEmailPage />} />

            <Route element={<PrivateLayout />}>
              <Route path="/create-delivery" element={<CreateDeliveryPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/logistics" element={<LogisticsPage />} />
              <Route path="/delivery-details/:id" element={<DeliveryDetailsPage />} />
              <Route path="/partners" element={<PartnersPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/approve-product/:id" element={<ApproveProductPage />} />
              <Route path="/product-details/:id" element={<ProductDetailsPage />} />
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/order-details/:id" element={<OrderDetailsPage />} />
              <Route path="/inventory" element={<InventoryPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </>
      ) : (
        <SplashContainer>
          <SplashScreen />
        </SplashContainer>
      )}
    </Fragment>
  );
}

export default App;
